import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DateRangeFilter from '../components/DateRangeFilter';
import VipRewardsModal from '../components/VipRewardsModal';
import DailyDetailModal from '../components/DailyDetailModal';
import { neonQuery } from '../services/api';

// VIP Cash Rewards — ledger-backed report.
//
// Data source: vip_cash_ledger (canonical, append-only, bank-style). Built
// from multiple source mirrors (mongo_earn / mongo_redeem / mongo_expire /
// mongo_cancellation / bc_order) and a small set of inferred adjustment
// rows (765 entries posted during the 2026-05 historical reconciliation).
// The ledger reconciles to BC store_credit to the penny.
//
// Daily 09:55 UTC append job pulls only NEW rows since the last watermark
// per source. Conflict + issue detection runs in the same handler.
//
// Rules panel writes to vip_cash_rules (documentation only — the program
// it describes lives in the legacy webapp and can't be derived from code).

const fmt$ = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtSigned = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) < 0.005) return '$0.00';
  return (v > 0 ? '+' : '−') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
// Parse YYYY-MM-DD as a calendar date (no timezone). The DB returns ET-
// bucketed days as DATE which serializes to "YYYY-MM-DDT00:00:00.000Z";
// new Date() then shifts that by the user's local offset and the day
// before shows up. Slice the YYYY-MM-DD prefix and format directly.
const fmtDateMMDDYY = (iso) => {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
  }
  return `${m[2]}/${m[3]}/${m[1].slice(-2)}`;
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDateLong = (iso) => {
  if (!iso) return '—';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return `${MONTHS[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}`;
};
const fmtRelative = (iso) => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  return `${Math.floor(diffMin / 1440)}d ago`;
};

function firstOfMonthIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
const todayIso = new Date().toISOString().split('T')[0];

const KPI_CARD = { padding: '12px 14px' };
const KPI_LABEL = { fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' };
const KPI_VALUE = { fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginTop: 2 };
const KPI_SUB = { fontSize: 10, color: 'var(--gray-500)', marginTop: 2 };

export default function VipCashReport() {
  const [start, setStart] = useState(firstOfMonthIso());
  const [end, setEnd] = useState(todayIso);

  const [booksHealth, setBooksHealth] = useState(null);
  const [activity, setActivity] = useState(null);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [rules, setRules] = useState([]);
  const [rulesEdits, setRulesEdits] = useState({});
  const [rulesOpen, setRulesOpen] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const pollTimerRef = useRef(null);
  const syncStartedAtRef = useRef(null);

  const [topHolders, setTopHolders] = useState([]);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [modalCustomer, setModalCustomer] = useState(null);
  const [drillDay, setDrillDay] = useState(null);
  const [rightTab, setRightTab] = useState('top'); // 'top' | 'conflicts' | 'issues' | 'inferred' | 'drift'
  const rightPanelRef = useRef(null);
  const jumpToTab = useCallback((tab) => {
    setRightTab(tab);
    requestAnimationFrame(() => rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);
  // Off by default — Monthly grants + their same-day reversals net to $0
  // but inflate Earns and Forfeits by ~$149K each, drowning out normal
  // activity. Toggle on to include them when investigating the grant pattern.
  const [includeMonthly, setIncludeMonthly] = useState(false);

  // Triage state
  const [conflictsList, setConflictsList] = useState([]);
  const [conflictsFilter, setConflictsFilter] = useState('open');
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [issuesList, setIssuesList] = useState([]);
  const [issuesFilter, setIssuesFilter] = useState('open');
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [inferredSummary, setInferredSummary] = useState([]);
  const [inferredList, setInferredList] = useState([]);
  const [inferredFilter, setInferredFilter] = useState('');
  const [inferredLoading, setInferredLoading] = useState(false);
  const [driftList, setDriftList] = useState([]);
  const [driftLoading, setDriftLoading] = useState(false);
  const [imbalanceList, setImbalanceList] = useState([]);
  const [imbalanceLoading, setImbalanceLoading] = useState(false);
  const [orphansList, setOrphansList] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);

  const reloadRules = useCallback(async () => {
    const rows = await neonQuery('vip_cash_rules_get');
    setRules(rows || []);
  }, []);

  const reloadStatus = useCallback(async () => {
    const rows = await neonQuery('vip_cash_sync_status');
    setSyncStatus(rows?.[0] || null);
  }, []);

  const [booksHealthVerifyStatus, setBooksHealthVerifyStatus] = useState(null);
  const reloadBooksHealth = useCallback(async () => {
    // Step 1: pull raw books_health
    const rows = await neonQuery('vip_cash_ledger_books_health');
    let h = rows?.[0] || null;
    setBooksHealth(h);
    if (!h) return;

    // Step 2: if there's apparent drift (and it's a tractable count), verify
    // by atomic-syncing each drifter, then re-pull. This eliminates false-
    // positive drift from snapshot-timing skew between mongo + BC mirrors.
    const drifted = Number(h.drifted_count) || 0;
    if (drifted === 0) { setBooksHealthVerifyStatus(null); return; }
    if (drifted > 50) {
      setBooksHealthVerifyStatus(`${drifted} drift candidates — too many to auto-verify on load. Open Drift tab to inspect.`);
      return;
    }

    setBooksHealthVerifyStatus(`Verifying ${drifted} drift candidate${drifted === 1 ? '' : 's'}…`);
    try {
      // pull current drift candidates
      const candidates = await neonQuery('vip_cash_ledger_drift_list', { mode: 'drift', limit: 50 }) || [];
      const ids = candidates.map(c => String(c.bc_customer_id));
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      // chunks of 5 to stay under BC rate limits
      for (let i = 0; i < ids.length; i += 5) {
        const chunk = ids.slice(i, i + 5);
        await Promise.all(chunk.map(bc =>
          fetch(`${base}/sync-vip-cash-rewards`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mode: 'customer', customerId: bc, force: true, triggeredBy: 'books-health-verify' }),
          }).catch(() => {})
        ));
        setBooksHealthVerifyStatus(`Verified ${Math.min(i + 5, ids.length)} of ${ids.length}…`);
      }
      // ledger append picks up any new rows the refreshes pulled
      await fetch(`${base}/sync-vip-cash-ledger-append-scheduled`, { method: 'POST', body: '{}' }).catch(() => {});

      // re-read books_health — now reflects truth
      const verified = await neonQuery('vip_cash_ledger_books_health');
      setBooksHealth(verified?.[0] || h);
      setBooksHealthVerifyStatus(null);
    } catch (e) {
      setBooksHealthVerifyStatus(null);
    }
  }, []);

  const reloadTopHolders = useCallback(async () => {
    const rows = await neonQuery('vip_cash_ledger_top_holders', { limit: 25 });
    setTopHolders(rows || []);
  }, []);

  const reloadConflicts = useCallback(async () => {
    setConflictsLoading(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_conflicts_list', { status: conflictsFilter, limit: 200 });
      setConflictsList(rows || []);
    } catch (e) { /* non-fatal */ }
    finally { setConflictsLoading(false); }
  }, [conflictsFilter]);

  const reloadIssues = useCallback(async () => {
    setIssuesLoading(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_issues_list', { status: issuesFilter, limit: 200 });
      setIssuesList(rows || []);
    } catch (e) { /* non-fatal */ }
    finally { setIssuesLoading(false); }
  }, [issuesFilter]);

  // Drift list is verified upstream during reloadBooksHealth — by the time
  // we read drift_list here, the per-customer atomic syncs have already run
  // and the data reflects truth. No re-verification needed.
  const reloadDrift = useCallback(async () => {
    setDriftLoading(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_drift_list', { mode: 'drift', limit: 500 });
      setDriftList(rows || []);
    } catch (e) { /* non-fatal */ }
    finally { setDriftLoading(false); }
  }, []);

  const reloadImbalance = useCallback(async () => {
    setImbalanceLoading(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_drift_list', { mode: 'imbalance', limit: 500 });
      setImbalanceList(rows || []);
    } catch (e) { /* non-fatal */ }
    finally { setImbalanceLoading(false); }
  }, []);

  const reloadOrphans = useCallback(async () => {
    setOrphansLoading(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_orphans_list');
      setOrphansList(rows || []);
    } catch (e) { /* non-fatal */ }
    finally { setOrphansLoading(false); }
  }, []);

  const reloadInferred = useCallback(async () => {
    setInferredLoading(true);
    try {
      const [summary, list] = await Promise.all([
        neonQuery('vip_cash_ledger_inferred_summary'),
        neonQuery('vip_cash_ledger_inferred_list', { source: inferredFilter, limit: 200 }),
      ]);
      setInferredSummary(summary || []);
      setInferredList(list || []);
    } catch (e) { /* non-fatal */ }
    finally { setInferredLoading(false); }
  }, [inferredFilter]);

  const runReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, d] = await Promise.all([
        neonQuery('vip_cash_ledger_activity_summary', { startDate: start, endDate: end, includeMonthly }),
        neonQuery('vip_cash_ledger_daily', { startDate: start, endDate: end, includeMonthly }),
      ]);
      setActivity(s?.[0] || null);
      setDaily(d || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [start, end, includeMonthly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await neonQuery('vip_cash_init_schema');
        if (cancelled) return;
        await Promise.all([
          reloadRules(), reloadStatus(), reloadBooksHealth(), reloadTopHolders(),
          reloadConflicts(), reloadIssues(), reloadInferred(), reloadDrift(), reloadImbalance(), reloadOrphans(),
          runReport(),
        ]);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { reloadConflicts(); }, [reloadConflicts]);
  useEffect(() => { reloadIssues(); }, [reloadIssues]);
  useEffect(() => { reloadInferred(); }, [reloadInferred]);
  useEffect(() => { reloadDrift(); }, [reloadDrift]);
  useEffect(() => { reloadImbalance(); }, [reloadImbalance]);
  // Auto-rerun the report when the monthly-grant toggle flips
  useEffect(() => { runReport(); }, [includeMonthly]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const rows = await neonQuery('vip_cash_latest_run');
        const run = rows?.[0] || null;
        if (!run) return;
        const startedAt = new Date(run.started_at).getTime();
        if (syncStartedAtRef.current && startedAt < syncStartedAtRef.current - 30000) return;
        setActiveRun(run);
        if (run.completed_at) {
          stopPolling();
          setSyncing(false);
          const r = run.result || {};
          if (run.error) {
            setSyncMessage(`Sync failed: ${run.error}`);
          } else {
            setSyncMessage(`Synced ${r.processed ?? 0}/${r.queued ?? 0} customers, +${r.inserted ?? 0} txns in ${r.elapsed_sec ?? '?'}s` + (r.timed_out ? ' (hit 14 min cap — click again to continue)' : ''));
          }
          // After sync, append fresh source rows to the ledger and refresh.
          try { await neonQuery('vip_cash_ledger_append'); } catch (e) { /* non-fatal */ }
          await Promise.all([
            reloadStatus(), reloadBooksHealth(), reloadTopHolders(),
            reloadConflicts(), reloadIssues(), runReport(),
          ]);
        } else {
          const r = run.result || {};
          if (r.queued) {
            setSyncMessage(`Sync running… ${r.processed ?? 0}/${r.queued} customers, +${r.inserted ?? 0} txns (${r.elapsed_sec ?? '?'}s)`);
          } else {
            setSyncMessage('Sync running… loading queue');
          }
          await reloadStatus();
        }
      } catch (e) { /* polling errors are non-fatal */ }
    }, 4000);
  }, [stopPolling, reloadStatus, reloadBooksHealth, reloadTopHolders, reloadConflicts, reloadIssues, runReport]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const runSync = async (mode) => {
    setSyncing(true);
    setSyncMessage('Starting background sync…');
    syncStartedAtRef.current = Date.now();
    setActiveRun(null);
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      const res = await fetch(`${base}/sync-vip-cash-rewards-background`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: mode || 'incremental', triggeredBy: 'ui-button' }),
      });
      if (res.status !== 202 && !res.ok) {
        const text = await res.text();
        if (text.startsWith('<')) throw new Error('Sync function returned HTML — deploy may still be propagating, retry in ~30 s.');
        throw new Error(`HTTP ${res.status}: ${text.slice(0, 120)}`);
      }
      startPolling();
    } catch (e) {
      setSyncMessage(`Sync failed: ${e.message}`);
      setSyncing(false);
    }
  };

  const saveRule = async (rule, value) => {
    setSavingKey(rule.rule_key);
    try {
      await neonQuery('vip_cash_rules_set', { rule_key: rule.rule_key, rule_value: value });
      await reloadRules();
      setRulesEdits((prev) => { const next = { ...prev }; delete next[rule.rule_key]; return next; });
    } catch (e) { setError(e.message); }
    finally { setSavingKey(null); }
  };

  const saveAllRules = async () => {
    const keys = Object.keys(rulesEdits);
    if (keys.length === 0) return;
    setSavingKey('__all__');
    try {
      for (const key of keys) {
        await neonQuery('vip_cash_rules_set', { rule_key: key, rule_value: rulesEdits[key] });
      }
      await reloadRules();
      setRulesEdits({});
    } catch (e) { setError(e.message); }
    finally { setSavingKey(null); }
  };

  const onSearch = async (q) => {
    setSearchQ(q);
    if (!q || q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const rows = await neonQuery('vip_cash_ledger_search_customers', { q: q.trim() });
      setSearchResults(rows || []);
    } catch (e) { setError(e.message); }
    finally { setSearching(false); }
  };

  const openCustomer = async (row) => {
    let storeCredit = null;
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      const res = await fetch(`${base}/bc-store-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: row.email }),
      });
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        if (res.ok && typeof data.storeCredit === 'number') storeCredit = data.storeCredit;
      } catch { /* HTML/SPA fallback during deploy */ }
    } catch { /* fall through with null */ }
    setModalCustomer({ bcCustomerId: row.bc_customer_id, customerName: row.name || row.email, storeCredit });
  };

  const dailyTotals = useMemo(() => {
    return daily.reduce((acc, r) => ({
      earns: acc.earns + Number(r.earns || 0),
      redemptions: acc.redemptions + Number(r.redemptions || 0),
      forfeits: acc.forfeits + Number(r.forfeits || 0),
      inferred_net: acc.inferred_net + Number(r.inferred_net || 0),
      entry_count: acc.entry_count + Number(r.entry_count || 0),
    }), { earns: 0, redemptions: 0, forfeits: 0, inferred_net: 0, entry_count: 0 });
  }, [daily]);

  const netActivity = useMemo(() => {
    if (!activity) return 0;
    return Number(activity.earns || 0) - Number(activity.redemptions || 0) - Number(activity.forfeits || 0);
  }, [activity]);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>VIP Cash Rewards</h2>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
            Canonical, bank-style ledger built from mongo + BC sources. Reconciled to BC store_credit; daily append at 09:55 UTC.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', textAlign: 'right' }}>
            <div>Mongo sync: <strong style={{ color: 'var(--navy)' }}>{fmtRelative(syncStatus?.last_run_at)}</strong></div>
            <div>Ledger appended: <strong style={{ color: 'var(--navy)' }}>{fmtRelative(booksHealth?.last_appended)}</strong></div>
          </div>
          <button onClick={() => runSync('incremental')} disabled={syncing} className="btn btn-navy" style={{ fontSize: 11 }}>
            {syncing ? 'Syncing…' : 'Run Sync Now'}
          </button>
        </div>
      </div>

      {syncMessage && (
        <div className="card" style={{ padding: '8px 14px', marginBottom: 12, fontSize: 11, color: 'var(--gray-700)', borderLeft: '3px solid var(--orange)' }}>
          {syncMessage}
        </div>
      )}

      <RulesPanel
        rules={rules}
        rulesEdits={rulesEdits}
        setRulesEdits={setRulesEdits}
        rulesOpen={rulesOpen}
        setRulesOpen={setRulesOpen}
        savingKey={savingKey}
        saveAll={saveAllRules}
      />

      {/* Books health card — lifetime, live */}
      <BooksHealthCard health={booksHealth} onJumpToTab={jumpToTab} verifyStatus={booksHealthVerifyStatus} />

      {/* Date range + run button */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={runReport} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
          <button onClick={() => { setStart(firstOfMonthIso()); setEnd(todayIso); }} className="btn" style={{ fontSize: 11 }}>This month</button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--gray-700)', marginLeft: 'auto', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeMonthly} onChange={(e) => setIncludeMonthly(e.target.checked)} />
            Include monthly grants &amp; their reversals
            <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>(off by default — these are a same-day issue/reverse pair that nets to $0 but inflates Earns and Forfeits)</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 12, marginBottom: 12, fontSize: 12, color: 'var(--red)', borderLeft: '3px solid var(--red)' }}>
          {error}
        </div>
      )}

      {/* Activity KPIs (range) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
        <div className="card" style={KPI_CARD}>
          <div style={KPI_LABEL}>Earns</div>
          <div style={{ ...KPI_VALUE, color: 'var(--green, #2e7d32)' }}>{fmt$(activity?.earns)}</div>
          <div style={KPI_SUB}>welcome, monthly, order, goodwill</div>
        </div>
        <div className="card" style={KPI_CARD}>
          <div style={KPI_LABEL}>Redemptions</div>
          <div style={{ ...KPI_VALUE, color: 'var(--orange)' }}>{fmt$(activity?.redemptions)}</div>
          <div style={KPI_SUB}>BC orders w/ store credit</div>
        </div>
        <div className="card" style={KPI_CARD}>
          <div style={KPI_LABEL}>Forfeits</div>
          <div style={{ ...KPI_VALUE, color: 'var(--gray-500)' }}>{fmt$(activity?.forfeits)}</div>
          <div style={KPI_SUB}>expired or VIP-cancelled</div>
        </div>
        <div className="card" style={KPI_CARD}>
          <div style={KPI_LABEL}>Adjustments</div>
          <div style={{ ...KPI_VALUE, color: '#6a1b9a' }}>{fmtSigned(activity?.inferred_net)}</div>
          <div style={KPI_SUB}>{fmtInt(activity?.inferred_count)} inferred entries</div>
        </div>
        <div className="card" style={KPI_CARD}>
          <div style={KPI_LABEL}>Net (range)</div>
          <div style={{ ...KPI_VALUE, color: netActivity >= 0 ? 'var(--navy)' : 'var(--red)' }}>{fmtSigned(netActivity)}</div>
          <div style={KPI_SUB}>earns − redemptions − forfeits</div>
        </div>
      </div>

      {/* Daily breakdown — from ledger */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Daily breakdown · {fmtDateLong(start)} → {fmtDateLong(end)}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading…</div>}
        {!loading && daily.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No ledger entries in the selected range.</div>
        )}
        {daily.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Earns</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Redemptions</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Forfeits</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Adjustments</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Net</th>
                  <th style={{ textAlign: 'left',  padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Sources</th>
                </tr>
              </thead>
              <tbody>
                {daily.map((row, i) => {
                  const net = Number(row.earns || 0) - Number(row.redemptions || 0) - Number(row.forfeits || 0);
                  return (
                    <tr key={i}
                        onClick={() => setDrillDay(row.day)}
                        style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>
                        <span style={{ borderBottom: '1px dashed rgba(0,59,92,0.35)' }}>{fmtDateMMDDYY(row.day)}</span>
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--green, #2e7d32)' }}>{fmt$(row.earns)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)' }}>{fmt$(row.redemptions)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt$(row.forfeits)}</td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', color: Math.abs(Number(row.inferred_net)) < 0.005 ? 'var(--gray-300, #d0d0d0)' : '#6a1b9a' }}>
                        {Math.abs(Number(row.inferred_net)) < 0.005 ? '—' : fmtSigned(row.inferred_net)}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: net >= 0 ? 'var(--navy)' : 'var(--red)' }}>{fmtSigned(net)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <SourceMix mongo={row.mongo_count} bc={row.bc_count} inferred={row.inferred_count} />
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background: 'var(--gray-100)', borderTop: '2px solid var(--gray-300)', fontWeight: 700 }}>
                  <td style={{ padding: '8px 12px', color: 'var(--navy)' }}>Totals</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--green, #2e7d32)' }}>{fmt$(dailyTotals.earns)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)' }}>{fmt$(dailyTotals.redemptions)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt$(dailyTotals.forfeits)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: '#6a1b9a' }}>{fmtSigned(dailyTotals.inferred_net)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--navy)' }}>{fmtSigned(dailyTotals.earns - dailyTotals.redemptions - dailyTotals.forfeits)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-700)' }}>{fmtInt(dailyTotals.entry_count)} entries</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Two-column: customer search + right panel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <div className="card">
          <div className="card-header">Customer lookup</div>
          <div style={{ padding: '10px 14px' }}>
            <input
              type="text"
              value={searchQ}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by email, name, or BC customer ID"
              style={{ width: '100%', fontSize: 11, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 4 }}
            />
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
              Searches the canonical ledger + BC v3 roster. Click a row for the per-customer ledger.
            </div>
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {searching && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Searching…</div>}
            {!searching && searchQ.trim().length >= 2 && searchResults.length === 0 && (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>No customers found.</div>
            )}
            {searchResults.length > 0 && (
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Balance</th>
                    <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Last txn</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((row) => (
                    <tr key={row.bc_customer_id} onClick={() => openCustomer(row)} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                      <td style={{ padding: '6px 12px' }}>
                        <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{row.name || '—'}</div>
                        <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{row.email || `BC #${row.bc_customer_id}`}</div>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: Number(row.balance) > 0 ? 'var(--green, #2e7d32)' : 'var(--gray-500)' }}>{fmt$(row.balance)}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmtDateMMDDYY(row.last_txn_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card" ref={rightPanelRef} style={{ scrollMarginTop: 12 }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)' }}>
            {[
              { key: 'top',       label: 'Top 25 balances' },
              { key: 'drift',     label: 'Drift',      badge: Number(booksHealth?.drifted_count) || 0 },
              { key: 'imbalance', label: 'Imbalance',  badge: Number(booksHealth?.imbalanced_count) || 0 },
              { key: 'conflicts', label: 'Conflicts',  badge: Number(booksHealth?.open_conflicts) || 0 },
              { key: 'issues',    label: 'Issues',     badge: Number(booksHealth?.open_issues) || 0 },
              { key: 'orphans',   label: 'Orphans', deemphasized: true },
            ].map((t) => {
              const active = rightTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setRightTab(t.key)}
                  style={{
                    background: 'transparent', border: 'none',
                    borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
                    padding: '10px 14px', fontSize: t.deemphasized ? 10 : 12,
                    fontWeight: active ? 700 : 600,
                    color: active ? 'var(--navy)' : (t.deemphasized ? 'var(--gray-400, #bdbdbd)' : 'var(--gray-500)'),
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    marginLeft: t.deemphasized ? 'auto' : 0,
                    fontStyle: t.deemphasized ? 'italic' : 'normal',
                  }}
                >
                  {t.label}
                  {t.badge > 0 && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: '#fff', background: 'var(--red, #c0392b)',
                      borderRadius: 9, padding: '1px 6px', minWidth: 14, textAlign: 'center',
                    }}>{t.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          {rightTab === 'top' && (
            <div style={{ maxHeight: 460, overflowY: 'auto' }}>
              {topHolders.length === 0 && (
                <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>No data yet.</div>
              )}
              {topHolders.length > 0 && (
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Balance</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Lifetime</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topHolders.map((row) => (
                      <tr key={row.bc_customer_id} onClick={() => openCustomer(row)} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                        <td style={{ padding: '6px 12px' }}>
                          <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{row.name || '—'}</div>
                          <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{row.email || `BC #${row.bc_customer_id}`}</div>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: 'var(--green, #2e7d32)' }}>{fmt$(row.balance)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt$(row.lifetime_issued)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {rightTab === 'drift' && (
            <DriftPanel
              rows={driftList}
              loading={driftLoading}
              openCustomer={openCustomer}
            />
          )}

          {rightTab === 'imbalance' && (
            <ImbalancePanel
              rows={imbalanceList}
              loading={imbalanceLoading}
              openCustomer={openCustomer}
            />
          )}

          {rightTab === 'orphans' && (
            <OrphansPanel
              rows={orphansList}
              loading={orphansLoading}
            />
          )}

          {rightTab === 'conflicts' && (
            <ConflictsPanel
              rows={conflictsList}
              loading={conflictsLoading}
              filter={conflictsFilter}
              setFilter={setConflictsFilter}
              onChange={async () => { await Promise.all([reloadConflicts(), reloadBooksHealth()]); }}
              openCustomer={openCustomer}
            />
          )}

          {rightTab === 'issues' && (
            <IssuesPanel
              rows={issuesList}
              loading={issuesLoading}
              filter={issuesFilter}
              setFilter={setIssuesFilter}
              onChange={async () => { await Promise.all([reloadIssues(), reloadBooksHealth()]); }}
              openCustomer={openCustomer}
            />
          )}

        </div>
      </div>

      <VipRewardsModal
        open={!!modalCustomer}
        onClose={() => setModalCustomer(null)}
        bcCustomerId={modalCustomer?.bcCustomerId}
        customerName={modalCustomer?.customerName}
        storeCredit={modalCustomer?.storeCredit}
        showDrift={true}
      />

      <DailyDetailModal
        open={!!drillDay}
        onClose={() => setDrillDay(null)}
        day={drillDay}
        onOpenCustomer={(c) => {
          setDrillDay(null);
          openCustomer({ bc_customer_id: c.bcCustomerId, name: c.customerName, email: c.customerEmail });
        }}
      />
    </div>
  );
}

// ── Books health card ──────────────────────────────────────────────────────

function BooksHealthCard({ health, onJumpToTab, verifyStatus }) {
  if (!health) {
    return (
      <div className="card" style={{ marginBottom: 12, padding: '12px 14px', fontSize: 12, color: 'var(--gray-500)' }}>
        Loading books health…
      </div>
    );
  }
  // ledger_balance is the sum of FIFO real_balances — the "true" total that
  // should reconcile to BC. Drift and Imbalance are DISTINCT metrics:
  //   drift     = FIFO real != BC store_credit          (ledger-vs-BC gap)
  //   imbalance = FIFO real > raw ledger sum            (ledger internally inconsistent)
  // A customer can be in both. orphan/test accounts are excluded upstream.
  const ledgerBal = Number(health.ledger_balance || 0);
  const bcBal = Number(health.bc_balance || 0);
  const driftAbsTotal = Number(health.drift_abs_total || 0);
  const imbalanceTotal = Number(health.imbalance_total || 0);
  const driftedCount = Number(health.drifted_count) || 0;
  const imbalancedCount = Number(health.imbalanced_count) || 0;
  const noDrift = driftedCount === 0;
  const noImbalance = imbalancedCount === 0;

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Books health · canonical ledger ↔ BC store credit</span>
        {verifyStatus && (
          <span style={{ fontSize: 11, fontWeight: 500, color: '#6d5b00', background: '#fffbe6', border: '1px solid #ffe082', padding: '2px 8px', borderRadius: 3 }}>
            {verifyStatus}
          </span>
        )}
      </div>
      <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <Stat label="Ledger total" value={fmt$(ledgerBal)} sub={`${fmtInt(health.row_count)} entries · FIFO real`} />
          <Stat label="BC total" value={fmt$(bcBal)} sub={`${fmtInt(health.bc_customers)} customers`} />
          <Stat
            label="Drift"
            value={verifyStatus ? '…' : (noDrift ? '$0.00 ✓' : fmt$(driftAbsTotal))}
            sub={verifyStatus ? 'verifying' : (noDrift ? 'ledger = BC' : `across ${fmtInt(driftedCount)} customers`)}
            color={verifyStatus ? 'var(--gray-500)' : (noDrift ? 'var(--green, #2e7d32)' : (driftAbsTotal > 500 ? 'var(--red, #c0392b)' : 'var(--orange)'))}
            onClick={() => onJumpToTab('drift')}
          />
          <Stat
            label="Imbalance"
            value={noImbalance ? '$0.00 ✓' : fmt$(imbalanceTotal)}
            sub={noImbalance ? 'ledgers reconcile' : `across ${fmtInt(imbalancedCount)} customers`}
            color={noImbalance ? 'var(--green, #2e7d32)' : 'var(--orange)'}
            onClick={() => onJumpToTab('imbalance')}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          <QueueBadge label="Conflicts" open={health.open_conflicts} total={health.total_conflicts} onClick={() => onJumpToTab('conflicts')} />
          <QueueBadge label="Issues"    open={health.open_issues}    total={health.total_issues}    onClick={() => onJumpToTab('issues')} />
        </div>
      </div>
      <div style={{ padding: '0 16px 10px', fontSize: 10, color: 'var(--gray-500)' }}>
        <strong>Drift</strong> = ledger ≠ BC store credit (a gap to close).
        <strong style={{ marginLeft: 10 }}>Imbalance</strong> = ledger internally inconsistent — redemptions exceed earns (needs an opening-balance entry).
        {' '}<strong style={{ marginLeft: 10 }}>Balanced</strong>: {fmtInt(health.balanced_count)} / {fmtInt(health.analyzed_count)} customers.
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color, onClick }) {
  return (
    <div onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div style={KPI_LABEL}>{label}</div>
      <div style={{ ...KPI_VALUE, color: color || 'var(--navy)', fontSize: 20 }}>{value}</div>
      {sub && <div style={KPI_SUB}>{sub}</div>}
    </div>
  );
}

function QueueBadge({ label, open, total, totalLabel, onClick, color }) {
  const hasOpen = (open || 0) > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: hasOpen ? '#fff8e1' : 'var(--gray-50)',
        border: `1px solid ${hasOpen ? '#ffe082' : 'var(--gray-200)'}`,
        borderRadius: 4, padding: '8px 10px', textAlign: 'left', cursor: 'pointer',
      }}
    >
      <div style={KPI_LABEL}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
        <span style={{ fontSize: 20, fontWeight: 800, color: hasOpen ? 'var(--red, #c0392b)' : (color || 'var(--navy)') }}>{fmtInt(open || 0)}</span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>open / {fmtInt(total || 0)} {totalLabel || 'total'}</span>
      </div>
    </button>
  );
}

// ── Source mix mini-badges (3 dots in the daily table) ─────────────────────

function SourceMix({ mongo, bc, inferred }) {
  const dots = [
    { c: '#2e7d32', n: Number(mongo) || 0, lbl: 'mongo' },
    { c: '#0d47a1', n: Number(bc) || 0,    lbl: 'BC' },
    { c: '#6a1b9a', n: Number(inferred) || 0, lbl: 'inferred' },
  ];
  return (
    <span style={{ display: 'inline-flex', gap: 8, fontSize: 10, color: 'var(--gray-500)' }}>
      {dots.map(d => (
        <span key={d.lbl} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          <span style={{ width: 6, height: 6, borderRadius: 3, background: d.n > 0 ? d.c : 'var(--gray-200, #e0e0e0)' }} />
          <span style={{ color: d.n > 0 ? 'var(--gray-700)' : 'var(--gray-400, #bdbdbd)' }}>{d.n}</span>
        </span>
      ))}
    </span>
  );
}

// ── Conflicts panel ────────────────────────────────────────────────────────

const CONFLICT_STATUSES = ['open','keep_a_only','keep_b_only','keep_both','keep_neither','acknowledged'];
const RESOLVE_OPTIONS = [
  { value: 'keep_a_only',  label: 'Keep A',     hint: 'exclude B from balance' },
  { value: 'keep_b_only',  label: 'Keep B',     hint: 'exclude A from balance' },
  { value: 'keep_both',    label: 'Keep both',  hint: 'not actually a conflict' },
  { value: 'keep_neither', label: 'Drop both',  hint: 'exclude both from balance' },
  { value: 'acknowledged', label: 'Acknowledge',hint: 'leave ledger untouched' },
];

// ── Orphans report (forensic, not in dashboard) ───────────────────────────

function OrphansPanel({ rows, loading }) {
  const totalWrittenOff = rows.reduce((s, r) => s + Math.max(0, Number(r.mongo_written_off || 0)), 0);
  const orphanCount = rows.filter(r => r.issue_type === 'orphan_account').length;
  const testCount   = rows.filter(r => r.issue_type === 'test_account').length;
  // Parse identity from summary
  const parseSummary = (r) => {
    const s = String(r.summary || '');
    if (r.issue_type === 'orphan_account') {
      const m = s.match(/Historical mongo identity:\s*(.+?)\.\s*Current BC owner:\s*(.+?)\./);
      return { line1: m?.[1] || '?', line2: m?.[2] || '?', kind: 'Reassigned' };
    }
    if (r.issue_type === 'test_account') {
      const m = s.match(/name matches test\|nivea:\s*(.+?)\./);
      return { line1: m?.[1] || '?', line2: '', kind: 'Test/sandbox' };
    }
    return { line1: '?', line2: '', kind: r.issue_type };
  };
  return (
    <div>
      <div style={{ padding: '8px 14px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', fontSize: 11, color: 'var(--gray-700)' }}>
        <strong>{rows.length}</strong> excluded accounts ({orphanCount} reassigned + {testCount} test/sandbox)
        {' · '}<strong>{fmt$(totalWrittenOff)}</strong> mongo balance written off
        <div style={{ color: 'var(--gray-500)', fontSize: 10, marginTop: 2 }}>
          Excluded from books-health, drift, top balances. Forensic record only.
        </div>
      </div>
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ padding: 16, fontSize: 11, color: 'var(--gray-500)', textAlign: 'center' }}>No excluded accounts.</div>
      )}
      {!loading && rows.length > 0 && (
        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left',  padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>BC#</th>
                <th style={{ textAlign: 'left',  padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Kind</th>
                <th style={{ textAlign: 'left',  padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Identity</th>
                <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Written off</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const s = parseSummary(r);
                return (
                  <tr key={r.bc_customer_id} style={{ borderTop: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '6px 12px', color: 'var(--gray-700)' }}>#{r.bc_customer_id}</td>
                    <td style={{ padding: '6px 8px',  color: 'var(--gray-500)', fontSize: 10 }}>{s.kind}</td>
                    <td style={{ padding: '6px 8px',  color: 'var(--gray-700)', fontSize: 10 }}>
                      <div>{s.line1}</div>
                      {s.line2 && <div style={{ color: 'var(--gray-500)' }}>→ {s.line2}</div>}
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt$(r.mongo_written_off)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Imbalance panel ───────────────────────────────────────────────────────
// Customers whose ledger is internally inconsistent: FIFO real_balance >
// raw ledger sum, meaning a redemption consumed more than was earned. The
// customer redeemed non-VIP store credit (refund / gift card / manual grant)
// that the mongo ledger never recorded. Fix = post an inferred_opening_balance.

function ImbalancePanel({ rows, loading, openCustomer }) {
  const totalImbalance = rows.reduce((s, r) => s + Number(r.imbalance || 0), 0);
  return (
    <div>
      <div style={{ padding: '8px 14px', background: '#fff8e1', borderBottom: '1px solid #ffe082', fontSize: 11, color: '#5d4037' }}>
        {rows.length === 0 ? (
          <span style={{ color: 'var(--gray-500)' }}>No imbalanced ledgers. ✓</span>
        ) : (
          <>
            <strong>{rows.length}</strong> customer{rows.length === 1 ? '' : 's'} with an internally-inconsistent ledger
            {' · '}<strong>{fmt$(totalImbalance)}</strong> total implied non-VIP credit
            <div style={{ color: 'var(--gray-500)', fontSize: 10, marginTop: 2 }}>
              FIFO real_balance &gt; raw ledger sum — a redemption consumed more than was earned. The customer
              redeemed credit (refund / gift card / manual BC grant) the mongo ledger never recorded. Fix: post an opening-balance entry.
            </div>
          </>
        )}
      </div>
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length > 0 && (
        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left',  padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                <th style={{ textAlign: 'right', padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Raw sum</th>
                <th style={{ textAlign: 'right', padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>FIFO real</th>
                <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Imbalance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.bc_customer_id} onClick={() => openCustomer({ bc_customer_id: r.bc_customer_id, name: r.name, email: r.email })} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                  <td style={{ padding: '6px 12px' }}>
                    <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{r.name || '—'}</div>
                    <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{r.email || `BC #${r.bc_customer_id}`}</div>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: Number(r.raw_sum) < 0 ? 'var(--red, #c0392b)' : 'var(--gray-700)' }}>{fmt$(r.raw_sum)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--navy)' }}>{fmt$(r.fifo_real)}</td>
                  <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--orange)' }}>{fmt$(r.imbalance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Drift panel ───────────────────────────────────────────────────────────

function DriftPanel({ rows, loading, openCustomer }) {
  const totalDrift = rows.reduce((s, r) => s + Math.abs(Number(r.drift || 0)), 0);
  return (
    <div>
      {loading && (
        <div style={{ padding: '8px 14px', background: '#fffbe6', borderBottom: '1px solid #ffe082', fontSize: 11, color: '#6d5b00' }}>
          Loading…
        </div>
      )}
      <div style={{ padding: '8px 14px', background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-100)', fontSize: 11, color: 'var(--gray-700)' }}>
        {rows.length === 0 ? (
          <span style={{ color: 'var(--gray-500)' }}>No customers in drift. ✓</span>
        ) : (
          <>
            <strong style={{ color: 'var(--red, #c0392b)' }}>{rows.length}</strong> customer{rows.length === 1 ? '' : 's'} where FIFO real_balance ≠ BC store_credit
            {' · '}<strong>{fmt$(totalDrift)}</strong> total drift
            <div style={{ color: 'var(--gray-500)', fontSize: 10, marginTop: 2 }}>
              Drift = BC − FIFO real. Positive = BC has more (overpaid). Negative = BC has less (underpaid). Internally-imbalanced ledgers show on the Imbalance tab, not here. orphan/test accounts excluded.
            </div>
          </>
        )}
      </div>
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length === 0 && null}
      {!loading && rows.length > 0 && (
        <div style={{ maxHeight: 460, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                <th style={{ textAlign: 'left',  padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                <th style={{ textAlign: 'right', padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>Ledger</th>
                <th style={{ textAlign: 'right', padding: '6px 8px',  fontWeight: 700, color: 'var(--gray-500)' }}>BC</th>
                <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Drift</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const drift = Number(r.drift || 0);
                const color = Math.abs(drift) > 50 ? 'var(--red, #c0392b)' : 'var(--orange)';
                return (
                  <tr key={r.bc_customer_id} onClick={() => openCustomer({ bc_customer_id: r.bc_customer_id, name: r.name, email: r.email })} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                    <td style={{ padding: '6px 12px' }}>
                      <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{r.name || '—'}</div>
                      <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{r.email || `BC #${r.bc_customer_id}`}</div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--navy)' }}>{fmt$(r.ledger_balance)}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{fmt$(r.bc_balance)}</td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color, whiteSpace: 'nowrap' }}>
                      {drift >= 0 ? '+' : '−'}{fmt$(Math.abs(drift))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConflictsPanel({ rows, loading, filter, setFilter, onChange, openCustomer }) {
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState({});
  const [err, setErr] = useState(null);

  const resolve = async (id, status) => {
    setBusyId(id); setErr(null);
    try {
      await neonQuery('vip_cash_ledger_conflict_resolve', {
        id, status, note: noteFor[id] || null, by: 'admin-ui',
      });
      setNoteFor((p) => { const n = { ...p }; delete n[id]; return n; });
      await onChange();
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Filter</span>
        <FilterChip active={filter === 'open'}  onClick={() => setFilter('open')}  label="Open" />
        <FilterChip active={filter === ''}      onClick={() => setFilter('')}      label="All" />
        {CONFLICT_STATUSES.filter(s => s !== 'open').map(s => (
          <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)} label={s.replace(/_/g, ' ')} />
        ))}
      </div>
      {err && <div style={{ padding: 8, fontSize: 11, color: 'var(--red, #c0392b)' }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ padding: 16, fontSize: 11, color: 'var(--gray-500)', textAlign: 'center' }}>
          {filter === 'open' ? 'No open conflicts. ✓' : 'No conflicts match this filter.'}
        </div>
      )}
      <div style={{ maxHeight: 460, overflowY: 'auto', padding: 8 }}>
        {rows.map(c => (
          <div key={c.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 4, padding: 10, marginBottom: 8, background: c.status === 'open' ? '#fff8e1' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12 }}>{c.conflict_type}</span>
              <StatusPill status={c.status} />
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--gray-500)' }}>{fmtDateMMDDYY(c.detected_at)}</span>
            </div>
            <div
              style={{ fontSize: 11, color: 'var(--navy)', marginBottom: 4, cursor: 'pointer' }}
              onClick={() => openCustomer({ bc_customer_id: c.bc_customer_id, name: c.name, email: c.email })}
            >
              <span style={{ borderBottom: '1px dashed rgba(0,59,92,0.35)' }}>{c.name || c.email || `BC #${c.bc_customer_id}`}</span>
            </div>
            {c.summary && <div style={{ fontSize: 11, color: 'var(--gray-700)', marginBottom: 6 }}>{c.summary}</div>}
            <div style={{ fontSize: 11, color: 'var(--gray-700)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>A: {c.source_a || '—'} {fmt$(c.amount_a)}</span>
              <span>B: {c.source_b || '—'} {fmt$(c.amount_b)}</span>
              {Math.abs(Number(c.delta || 0)) > 0 && <span><strong>Δ</strong> {fmtSigned(c.delta)}</span>}
            </div>
            {c.status === 'open' && (
              <div style={{ marginTop: 8, padding: 8, borderTop: '1px dashed var(--gray-200)' }}>
                <input
                  type="text"
                  placeholder="Resolution note (optional)"
                  value={noteFor[c.id] || ''}
                  onChange={(e) => setNoteFor({ ...noteFor, [c.id]: e.target.value })}
                  style={{ width: '100%', fontSize: 11, padding: '4px 8px', border: '1px solid var(--gray-200)', borderRadius: 3, marginBottom: 6 }}
                />
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {RESOLVE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      disabled={busyId === c.id}
                      onClick={() => resolve(c.id, opt.value)}
                      className="btn"
                      style={{ fontSize: 10, padding: '3px 8px' }}
                      title={opt.hint}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {c.status !== 'open' && c.resolution_note && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-700)', fontStyle: 'italic' }}>
                "{c.resolution_note}"{c.resolved_by ? <span style={{ color: 'var(--gray-500)' }}> — {c.resolved_by}</span> : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Issues panel ───────────────────────────────────────────────────────────

const ISSUE_STATUSES = ['open','acknowledged','resolved','dismissed'];
const ISSUE_RESOLVE_OPTIONS = [
  { value: 'resolved',     label: 'Resolve' },
  { value: 'acknowledged', label: 'Acknowledge' },
  { value: 'dismissed',    label: 'Dismiss' },
];

function IssuesPanel({ rows, loading, filter, setFilter, onChange, openCustomer }) {
  const [busyId, setBusyId] = useState(null);
  const [noteFor, setNoteFor] = useState({});
  const [err, setErr] = useState(null);

  const resolve = async (id, status) => {
    setBusyId(id); setErr(null);
    try {
      await neonQuery('vip_cash_ledger_issue_resolve', {
        id, status, note: noteFor[id] || null, by: 'admin-ui',
      });
      setNoteFor((p) => { const n = { ...p }; delete n[id]; return n; });
      await onChange();
    } catch (e) { setErr(e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>Filter</span>
        <FilterChip active={filter === 'open'} onClick={() => setFilter('open')} label="Open" />
        <FilterChip active={filter === ''}     onClick={() => setFilter('')}     label="All" />
        {ISSUE_STATUSES.filter(s => s !== 'open').map(s => (
          <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)} label={s} />
        ))}
      </div>
      {err && <div style={{ padding: 8, fontSize: 11, color: 'var(--red, #c0392b)' }}>{err}</div>}
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ padding: 16, fontSize: 11, color: 'var(--gray-500)', textAlign: 'center' }}>
          {filter === 'open' ? 'No open issues. ✓' : 'No issues match this filter.'}
        </div>
      )}
      <div style={{ maxHeight: 460, overflowY: 'auto', padding: 8 }}>
        {rows.map(i => (
          <div key={i.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 4, padding: 10, marginBottom: 8, background: i.status === 'open' ? '#fff8e1' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12 }}>{i.issue_type}</span>
              <SeverityPill severity={i.severity} />
              <StatusPill status={i.status} />
              <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--gray-500)' }}>{fmtDateMMDDYY(i.detected_at)}</span>
            </div>
            <div
              style={{ fontSize: 11, color: 'var(--navy)', marginBottom: 4, cursor: 'pointer' }}
              onClick={() => openCustomer({ bc_customer_id: i.bc_customer_id, name: i.name, email: i.email })}
            >
              <span style={{ borderBottom: '1px dashed rgba(0,59,92,0.35)' }}>{i.name || i.email || `BC #${i.bc_customer_id}`}</span>
            </div>
            {i.summary && <div style={{ fontSize: 11, color: 'var(--gray-700)', marginBottom: 6 }}>{i.summary}</div>}
            {i.ledger_amount && (
              <div style={{ fontSize: 11, color: 'var(--gray-700)' }}>
                {i.ledger_source && <span style={{ marginRight: 6 }}>{i.ledger_source}</span>}
                <strong>{i.ledger_direction === 'credit' ? '+' : '−'}{fmt$(i.ledger_amount)}</strong>
              </div>
            )}
            {i.status === 'open' && (
              <div style={{ marginTop: 8, padding: 8, borderTop: '1px dashed var(--gray-200)' }}>
                <input
                  type="text"
                  placeholder="Resolution note (optional)"
                  value={noteFor[i.id] || ''}
                  onChange={(e) => setNoteFor({ ...noteFor, [i.id]: e.target.value })}
                  style={{ width: '100%', fontSize: 11, padding: '4px 8px', border: '1px solid var(--gray-200)', borderRadius: 3, marginBottom: 6 }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  {ISSUE_RESOLVE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      disabled={busyId === i.id}
                      onClick={() => resolve(i.id, opt.value)}
                      className="btn"
                      style={{ fontSize: 10, padding: '3px 8px' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {i.status !== 'open' && i.resolution_note && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-700)', fontStyle: 'italic' }}>
                "{i.resolution_note}"{i.resolved_by ? <span style={{ color: 'var(--gray-500)' }}> — {i.resolved_by}</span> : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Inferred entries panel ─────────────────────────────────────────────────

function InferredPanel({ summary, rows, loading, filter, setFilter, openCustomer }) {
  return (
    <div>
      <div style={{ padding: '8px 14px', background: '#f3e5f5', fontSize: 11, color: '#4a148c', borderBottom: '1px solid #ce93d8' }}>
        Synthetic ledger entries posted during the 2026-05 historical reconciliation to make the books balance. Read-only context — not actionable.
      </div>
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <FilterChip active={filter === ''} onClick={() => setFilter('')} label="All" />
        {summary.map(s => (
          <FilterChip
            key={s.source}
            active={filter === s.source}
            onClick={() => setFilter(s.source)}
            label={`${s.source.replace('inferred_', '')} (${s.entry_count})`}
          />
        ))}
      </div>
      {loading && <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>}
      {!loading && rows.length === 0 && (
        <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>No inferred entries match.</div>
      )}
      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} onClick={() => openCustomer({ bc_customer_id: r.bc_customer_id, name: r.name, email: r.email })} style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }}>
                <td style={{ padding: '6px 12px' }}>
                  <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{r.name || '—'}</div>
                  <div style={{ color: 'var(--gray-500)', fontSize: 10 }}>{r.email || `BC #${r.bc_customer_id}`}</div>
                </td>
                <td style={{ padding: '6px 8px', color: '#6a1b9a', fontSize: 10 }}>{r.source.replace('inferred_', '')}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: r.direction === 'credit' ? 'var(--green, #2e7d32)' : 'var(--orange)' }}>
                  {r.direction === 'credit' ? '+' : '−'}{fmt$(r.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Shared bits ────────────────────────────────────────────────────────────

function FilterChip({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 10, fontWeight: 600,
        padding: '3px 8px', borderRadius: 10, cursor: 'pointer',
        background: active ? 'var(--navy)' : '#fff',
        color: active ? '#fff' : 'var(--gray-700)',
        border: `1px solid ${active ? 'var(--navy)' : 'var(--gray-200)'}`,
        textTransform: 'capitalize',
      }}
    >{label}</button>
  );
}

const STATUS_COLORS = {
  open:         { bg: '#fff3e0', fg: '#e65100' },
  keep_a_only:  { bg: '#e8f5e9', fg: '#2e7d32' },
  keep_b_only:  { bg: '#e8f5e9', fg: '#2e7d32' },
  keep_both:    { bg: '#e3f2fd', fg: '#0d47a1' },
  keep_neither: { bg: '#fafafa', fg: '#616161' },
  acknowledged: { bg: '#fafafa', fg: '#616161' },
  resolved:     { bg: '#e8f5e9', fg: '#2e7d32' },
  dismissed:    { bg: '#fafafa', fg: '#9e9e9e' },
};
function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || { bg: '#fafafa', fg: '#616161' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 700, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 9, whiteSpace: 'nowrap',
      background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{String(status || '').replace(/_/g, ' ')}</span>
  );
}

const SEVERITY_COLORS = {
  needs_action: { bg: '#ffebee', fg: '#c62828' },
  warn:         { bg: '#fff3e0', fg: '#e65100' },
  info:         { bg: '#e3f2fd', fg: '#0d47a1' },
};
function SeverityPill({ severity }) {
  const s = SEVERITY_COLORS[severity] || { bg: '#fafafa', fg: '#616161' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 700, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 9, whiteSpace: 'nowrap',
      background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{severity}</span>
  );
}

// ── Rules panel (unchanged) ────────────────────────────────────────────────

const I_LABEL = { fontSize: 11, color: 'var(--gray-700)', display: 'inline-flex', alignItems: 'center', gap: 6 };
const I_INPUT = { fontSize: 11, padding: '3px 6px', border: '1px solid var(--gray-200)', borderRadius: 4, lineHeight: 1.3 };
const SECTION_TITLE = { fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 };

const isTruthy = (v) => v === 'true' || v === '1' || v === true;

function useRuleHelpers(rules, rulesEdits, setRulesEdits) {
  const map = useMemo(() => {
    const m = {};
    for (const r of rules) m[r.rule_key] = r;
    return m;
  }, [rules]);
  const valueOf = (key, fallback = '') => {
    if (rulesEdits[key] !== undefined) return rulesEdits[key];
    return map[key]?.rule_value ?? fallback;
  };
  const setValue = (key, v) => setRulesEdits((p) => ({ ...p, [key]: v }));
  return { map, valueOf, setValue };
}

function MoneyInput({ value, onChange, width = 80 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>$</span>
      <input type="text" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ ...I_INPUT, width }} placeholder="0.00" />
    </span>
  );
}
function PercentInput({ value, onChange, width = 60 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <input type="text" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ ...I_INPUT, width }} placeholder="0" />
      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>%</span>
    </span>
  );
}
function DaysInput({ value, onChange, width = 60 }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <input type="text" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ ...I_INPUT, width }} placeholder="0" />
      <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>days</span>
    </span>
  );
}
function BoolInput({ value, onChange, label }) {
  const checked = isTruthy(value);
  return (
    <label style={{ ...I_LABEL, cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
      {label}
    </label>
  );
}

function RulesPanel({ rules, rulesEdits, setRulesEdits, rulesOpen, setRulesOpen, savingKey, saveAll }) {
  const { valueOf, setValue } = useRuleHelpers(rules, rulesEdits, setRulesEdits);
  const dirtyCount = Object.keys(rulesEdits).length;
  const saving = savingKey === '__all__';

  const welcomeExpires = isTruthy(valueOf('welcome_expires'));
  const cashbackExpires = isTruthy(valueOf('cashback_expires'));

  const perks = [
    { enabledKey: 'perk_free_ground',     minKey: 'perk_free_ground_min',     label: 'Free Ground Shipping' },
    { enabledKey: 'perk_air_discount',    minKey: 'perk_air_discount_min',    label: '$20 Off Air Shipping' },
    { enabledKey: 'perk_free_insurance',  minKey: 'perk_free_insurance_min',  label: 'Free UPS Insurance' },
    { enabledKey: 'perk_free_packaging',  minKey: 'perk_free_packaging_min',  label: 'Free Packaging' },
  ];

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setRulesOpen((v) => !v)}>
        <span>Cash Rules · what generates VIP cash</span>
        <span style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 400 }}>{rulesOpen ? '▼ hide' : '▶ show'}</span>
      </div>
      {rulesOpen && rules.length === 0 && (
        <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading rules…</div>
      )}
      {rulesOpen && rules.length > 0 && (
        <>
          <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 18, alignItems: 'start' }}>
            <div>
              <div style={SECTION_TITLE}>Welcome Bonus</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={I_LABEL}>
                  <span style={{ width: 60 }}>Amount</span>
                  <MoneyInput value={valueOf('welcome_amount')} onChange={(v) => setValue('welcome_amount', v)} />
                </div>
                <BoolInput
                  value={valueOf('welcome_expires')}
                  onChange={(v) => setValue('welcome_expires', v)}
                  label={
                    <>Expires after{' '}
                      {welcomeExpires
                        ? <DaysInput value={valueOf('welcome_expiry_days')} onChange={(v) => setValue('welcome_expiry_days', v)} />
                        : <span style={{ color: 'var(--gray-400)' }}>(N) days</span>}
                    </>
                  }
                />
              </div>
            </div>
            <div>
              <div style={SECTION_TITLE}>Cash Earned per Order</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={I_LABEL}>
                  <span style={{ width: 60 }}>Cashback</span>
                  <PercentInput value={valueOf('cashback_pct')} onChange={(v) => setValue('cashback_pct', v)} />
                  <span style={{ marginLeft: 8 }}>min cart</span>
                  <MoneyInput value={valueOf('cashback_min_cart')} onChange={(v) => setValue('cashback_min_cart', v)} />
                </div>
                <BoolInput
                  value={valueOf('cashback_expires')}
                  onChange={(v) => setValue('cashback_expires', v)}
                  label={
                    <>Expires after{' '}
                      {cashbackExpires
                        ? <DaysInput value={valueOf('cashback_expiry_days')} onChange={(v) => setValue('cashback_expiry_days', v)} />
                        : <span style={{ color: 'var(--gray-400)' }}>(N) days</span>}
                    </>
                  }
                />
              </div>
            </div>
            <div>
              <div style={SECTION_TITLE}>Cancellation</div>
              <BoolInput
                value={valueOf('cancel_rewards_on_expire')}
                onChange={(v) => setValue('cancel_rewards_on_expire', v)}
                label="Cancel all rewards when a VIP membership expires"
              />
            </div>
            <div>
              <div style={SECTION_TITLE}>VIP Perks</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {perks.map((p) => {
                  const enabled = isTruthy(valueOf(p.enabledKey));
                  return (
                    <div key={p.enabledKey} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BoolInput value={valueOf(p.enabledKey)} onChange={(v) => setValue(p.enabledKey, v)} label={p.label} />
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: enabled ? 'var(--gray-700)' : 'var(--gray-400)' }}>
                        min cart{' '}
                        <MoneyInput value={valueOf(p.minKey)} onChange={(v) => setValue(p.minKey, v)} width={70} />
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
            {dirtyCount > 0 && (
              <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
              </span>
            )}
            <button
              onClick={saveAll}
              disabled={dirtyCount === 0 || saving}
              className={dirtyCount > 0 ? 'btn btn-primary' : 'btn'}
              style={{ fontSize: 11, opacity: dirtyCount > 0 ? 1 : 0.5 }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
